import { useEffect, useCallback, type ReactNode } from 'react';
import { useAppSelector, useAppDispatch } from '@core/store';
import { removeToast, type Toast as ToastType } from '@core/store/slices/uiSlice';
import {
    ToastProvider as RadixToastProvider,
    ToastViewport,
    Toast,
    ToastTitle,
    ToastDescription,
    ToastClose,
    ToastIcon,
} from './Toast';

interface ToastProviderProps {
    children: ReactNode;
}

/**
 * Toast Provider Component
 *
 * Wraps the application and provides toast notifications
 * from the Redux store's UI slice.
 *
 * @example
 * // In App.tsx
 * <ToastProvider>
 *   <RouterProvider router={router} />
 * </ToastProvider>
 *
 * @example
 * // To show a toast from any component
 * dispatch(addToast({ type: 'success', title: 'Success!', message: 'Item saved.' }));
 */
export function ToastProvider({ children }: ToastProviderProps) {
    const dispatch = useAppDispatch();
    const toasts = useAppSelector((state) => state.ui.toasts);

    /**
     * Handle toast close
     */
    const handleClose = useCallback(
        (id: string) => {
            dispatch(removeToast(id));
        },
        [dispatch]
    );

    /**
     * Auto-dismiss toasts after their duration
     */
    useEffect(() => {
        const timers: ReturnType<typeof setTimeout>[] = [];

        toasts.forEach((toast) => {
            const timer = setTimeout(() => {
                dispatch(removeToast(toast.id));
            }, toast.duration ?? 5000);

            timers.push(timer);
        });

        return () => {
            timers.forEach(clearTimeout);
        };
    }, [toasts, dispatch]);

    /**
     * Map toast type to variant
     */
    const getVariant = (type: ToastType['type']) => {
        switch (type) {
            case 'success':
                return 'success';
            case 'error':
                return 'error';
            case 'warning':
                return 'warning';
            case 'info':
                return 'info';
            default:
                return 'default';
        }
    };

    return (
        <RadixToastProvider swipeDirection="right">
            {children}

            {toasts.map((toast) => (
                <Toast
                    key={toast.id}
                    variant={getVariant(toast.type)}
                    onOpenChange={(open) => {
                        if (!open) handleClose(toast.id);
                    }}
                >
                    <div className="flex items-start gap-3">
                        <ToastIcon variant={getVariant(toast.type)} />
                        <div className="grid gap-1">
                            <ToastTitle>{toast.title}</ToastTitle>
                            {toast.message && (
                                <ToastDescription>{toast.message}</ToastDescription>
                            )}
                        </div>
                    </div>
                    <ToastClose />
                </Toast>
            ))}

            <ToastViewport />
        </RadixToastProvider>
    );
}

export default ToastProvider;
